import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Collapse,
    Flex,
    Grid,
    HStack,
    Icon,
    IconButton,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Portal,
    Select,
    Text,
    VStack,
    useBreakpointValue,
    useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  MdBookmark,
  MdBookmarkBorder,
  MdCalendarToday,
  MdCheck,
  MdClose,
  MdExpandLess,
  MdExpandMore,
  MdFilterList,
  MdRefresh,
  MdSearch,
  MdScale,
  MdRoute,
} from 'react-icons/md';
import { FaRoute } from 'react-icons/fa';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import AiAssistantDrawer from 'domains/ai/components/AiAssistantDrawer';
import {
  UnifiedEmptyState,
  UnifiedFilterBar,
    UnifiedLoadingState,
    UnifiedPageToolbar,
    UnifiedResultsSection,
    UnifiedSurfaceCard,
    useUnifiedButtonStyles,
    useUnifiedInteractiveCardStyles,
    useUnifiedSurfaceStyles,
} from 'components/layout/UnifiedPrimitives';
import {
  getListings,
  toggleWatch,
} from 'domains/listing/services/listingService';
import ListingItemsSection from 'domains/listing/components/ListingItemsSection';
import { consumePartnerMarketplaceFilters } from 'domains/ai/utils/storage';
import { useTranslation } from 'i18n/TranslationProvider';
import { translateRaw } from 'i18n/globalTranslator';
import { formatDateWithOptions } from 'utilities/dateTimeUtils';
import { mapTemplateTitleToTranslation , CATEGORIES} from 'domains/shared/utils/categoryUtils';


const EU_COUNTRIES = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  'United Kingdom',
  'Norway',
  'Switzerland',
  'Ukraine',
  'Turkey',
  'Serbia',
  'Albania',
  'North Macedonia',
  'Bosnia',
  'Montenegro',
];

const PAGE_SIZE = 20;
const EMPTY_FILTERS = {
    categories: [],
    fromCountry: '',
    fromCity: '',
    fromPostcode: '',
    toCountry: '',
    toCity: '',
    toPostcode: '',
    pickupDateFrom: '',
    pickupDateTo: '',
    search: '',
    radiusKm: '',
    toRadiusKm: '',
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function formatDateRange(from, to) {
  if (!from && !to) return translateRaw('Flexible dates');
  const fmt = (date) =>
    date
      ? formatDateWithOptions(date, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '?';
  if (from && to && from !== to) return `${fmt(from)} – ${fmt(to)}`;
  return fmt(from || to);
}

function formatCategoryLabel(value) {
  return String(value || 'other').replace(/_/g, ' ');
}

function getCategoriesLabel(selected) {
  if (!selected.length) return translateRaw('All categories');
  if (selected.length === 1) {
    return translateRaw(
      CATEGORIES.find((option) => option.id === selected[0])?.label ||
        'All categories',
    );
  }
  return translateRaw('{{count}} categories', { count: selected.length });
}

function getActiveFilterSummary(filters) {
  const chips = [];

  if (filters.categories.length) {
    chips.push({
      key: 'categories',
      label: getCategoriesLabel(filters.categories),
    });
  }
  if (filters.fromCountry || filters.fromCity || filters.fromPostcode) {
    const radiusLabel = filters.radiusKm ? ` · ${filters.radiusKm} km` : '';
    chips.push({
      key: 'from',
      label: `${translateRaw('Pick-up')}: ${[filters.fromCity, filters.fromCountry, filters.fromPostcode].filter(Boolean).join(', ')}${radiusLabel}`,
    });
  }
  if (filters.toCountry || filters.toCity || filters.toPostcode) {
    chips.push({
      key: 'to',
      label: `${translateRaw('Delivery')}: ${[filters.toCity, filters.toCountry, filters.toPostcode].filter(Boolean).join(', ')}`,
    });
  }
  if (filters.pickupDateFrom || filters.pickupDateTo) {
    chips.push({
      key: 'dates',
      label: `${translateRaw('Pick-up window')}: ${formatDateRange(filters.pickupDateFrom, filters.pickupDateTo)}`,
    });
  }
  if (filters.search) {
    chips.push({
      key: 'search',
      label: `${translateRaw('Keyword')}: ${filters.search}`,
    });
  }

  return chips;
}

function getItemDetails(listing) {
  const items = Array.isArray(listing.items)
    ? listing.items.filter(Boolean)
    : [];
  if (!items.length)
    return {
      title: translateRaw('No item details'),
      facts: [],
      dimensions: null,
    };

  const first = items[0] || {};
  const dimensionSource =
    items.find(
      (entry) =>
        entry?.weight || entry?.length || entry?.width || entry?.height,
    ) || first;
  // Prefix year on vehicle cards so partners scanning search results see
  // "2019 BMW X5" and can judge fit at a glance before opening the detail.
  const title =
    [first.year, first.make, first.model].filter(Boolean).join(' ').trim() ||
    first.itemDescription ||
    first.petType ||
    first.category ||
    translateRaw('Shipment details');

  const facts = [];
  if (items.length)
    facts.push(translateRaw('{{count}} item(s)', { count: items.length }));
  if (first.quantity) facts.push(`${translateRaw('Qty')} ${first.quantity}`);
  if (first.weight) facts.push(`${first.weight} kg`);
  if ([first.length, first.width, first.height].some(Boolean)) {
    facts.push(
      [first.length, first.width, first.height].filter(Boolean).join(' × ') +
        ' cm',
    );
  }
  if (first.status || first.vehicleStatus) {
    const normalized = String(
      first.status || first.vehicleStatus,
    ).toLowerCase();
    if (normalized === 'operable' || normalized === 'operational')
      facts.push(translateRaw('Operable'));
    if (
      normalized === 'non-operable' ||
      normalized === 'nonoperable' ||
      normalized === 'inoperable'
    )
      facts.push(translateRaw('Non-operable'));
  }

  // Extract detailed dimensions for display
  const dimensions = {
    weight: dimensionSource.weight,
    length: dimensionSource.length,
    width: dimensionSource.width,
    height: dimensionSource.height,
    itemCount: items.length,
  };

  return { title, facts: facts.slice(0, 4), dimensions };
}

function FilterFields({ filters, setFilter, inputBg, debouncedToCity, t }) {
    return (
        <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={4} w="100%">
            <Box>
                <Text fontSize="xs" fontWeight="800" color="brand.500" textTransform="uppercase" mb={2}>
                    {t('Pick-up location')}
                </Text>
                <Select
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    mb={2}
                    placeholder={t('Any country')}
                    value={filters.fromCountry}
                    onChange={(e) => setFilter('fromCountry', e.target.value)}
                >
                    {EU_COUNTRIES.map((country) => (
                        <option key={country} value={country}>{country}</option>
                    ))}
                </Select>
                <Input
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('City')}
                    mb={2}
                    value={filters.fromCity}
                    onChange={(e) => setFilter('fromCity', e.target.value)}
                />
                <Input
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('Postcode')}
                    mb={2}
                    value={filters.fromPostcode}
                    onChange={(e) => setFilter('fromPostcode', e.target.value)}
                />
                <Select
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('Any radius')}
                    value={filters.radiusKm}
                    onChange={(e) => setFilter('radiusKm', e.target.value)}
                    isDisabled={!filters.fromCity}
                >
                    <option value="25">25 km</option>
                    <option value="50">50 km</option>
                    <option value="100">100 km</option>
                    <option value="200">200 km</option>
                    <option value="500">500 km</option>
                </Select>
            </Box>

            <Box>
                <Text fontSize="xs" fontWeight="800" color="brand.500" textTransform="uppercase" mb={2}>
                    {t('Delivery location')}
                </Text>
                <Select
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    mb={2}
                    placeholder={t('Any country')}
                    value={filters.toCountry}
                    onChange={(e) => setFilter('toCountry', e.target.value)}
                >
                    {EU_COUNTRIES.map((country) => (
                        <option key={country} value={country}>{country}</option>
                    ))}
                </Select>
                <Input
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('City')}
                    mb={2}
                    value={filters.toCity}
                    onChange={(e) => setFilter('toCity', e.target.value)}
                />
                <Input
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('Postcode')}
                    mb={2}
                    value={filters.toPostcode}
                    onChange={(e) => setFilter('toPostcode', e.target.value)}
                />
                <Select
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('Delivery radius')}
                    value={filters.toRadiusKm}
                    onChange={(e) => setFilter('toRadiusKm', e.target.value)}
                    isDisabled={!debouncedToCity}
                >
                    <option value="25">25 km</option>
                    <option value="50">50 km</option>
                    <option value="100">100 km</option>
                    <option value="200">200 km</option>
                    <option value="500">500 km</option>
                </Select>
            </Box>

            <Box>
                <Text fontSize="xs" fontWeight="800" color="brand.500" textTransform="uppercase" mb={2}>
                    {t('Pick-up window')}
                </Text>
                <HStack mb={4}>
                    <HStack
                        spacing={2}
                        px={3}
                        py={2}
                        borderRadius="14px"
                        bg={inputBg}
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        flex="1"
                    >
                        <Icon as={MdCalendarToday} color="brand.500" />
                        <Input
                            size="sm"
                            variant="unstyled"
                            type="date"
                            value={filters.pickupDateFrom}
                            onChange={(e) => setFilter('pickupDateFrom', e.target.value)}
                        />
                    </HStack>
                    <HStack
                        spacing={2}
                        px={3}
                        py={2}
                        borderRadius="14px"
                        bg={inputBg}
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        flex="1"
                    >
                        <Icon as={MdCalendarToday} color="brand.500" />
                        <Input
                            size="sm"
                            variant="unstyled"
                            type="date"
                            value={filters.pickupDateTo}
                            onChange={(e) => setFilter('pickupDateTo', e.target.value)}
                        />
                    </HStack>
                </HStack>
                <Text fontSize="xs" fontWeight="800" color="brand.500" textTransform="uppercase" mb={2}>
                    {t('Keyword')}
                </Text>
                <Input
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('Title, item, route, description...')}
                    value={filters.search}
                    onChange={(e) => setFilter('search', e.target.value)}
                />
            </Box>
        </Grid>
    );
}

function formatDistance(distanceKm) {
  if (!distanceKm) return null;
  const km = Number(distanceKm);
  if (isNaN(km)) return null;
  if (km < 1) return `${(km * 1000).toFixed(0)} ${translateRaw('m')}`;
  if (km < 10) return `${km.toFixed(1)} ${translateRaw('booking.km')}`;
  return `${Math.round(km)} ${translateRaw('booking.km')}`;
}

function formatMoney(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? `EUR ${numeric.toFixed(2)}`
    : null;
}

function formatListingStatus(value) {
  const normalized = String(value || 'open')
    .replace(/_/g, ' ')
    .trim();
  return normalized
    ? translateRaw(normalized.toUpperCase())
    : translateRaw('OPEN');
}

function getMapThumbnailUrl(listing) {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const origin = listing.fromCoords || listing.publicFromCoords;
  const destination = listing.toCoords || listing.publicToCoords;
  if (!apiKey || (!origin && !destination)) return null;

  const parts = ['size=240x180', 'scale=2', 'maptype=roadmap', `key=${apiKey}`];
  if (origin)
    parts.push(`markers=color:0x4285F4|label:A|${origin.lat},${origin.lng}`);
  if (destination)
    parts.push(
      `markers=color:0xEA4335|label:B|${destination.lat},${destination.lng}`,
    );

  if (origin && destination) {
    const latDiff = Math.abs(origin.lat - destination.lat);
    const lngDiff = Math.abs(origin.lng - destination.lng);
    const MIN_SPAN = 0.14;
    const effectiveLat = Math.max(latDiff, MIN_SPAN);
    const effectiveLng = Math.max(lngDiff, MIN_SPAN);
    const PAD = 0.45;
    const minLat = Math.min(origin.lat, destination.lat) - effectiveLat * PAD;
    const maxLat = Math.max(origin.lat, destination.lat) + effectiveLat * PAD;
    const minLng = Math.min(origin.lng, destination.lng) - effectiveLng * PAD;
    const maxLng = Math.max(origin.lng, destination.lng) + effectiveLng * PAD;
    parts.push(`visible=${minLat},${minLng}|${maxLat},${maxLng}`);
  } else {
    const point = origin || destination;
    parts.push(`center=${point.lat},${point.lng}`);
    parts.push('zoom=9');
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${parts.join('&')}`;
}

function ResultCard({ listing }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { subtleBg, subtleBorder, mutedText } = useUnifiedSurfaceStyles();
    const { primaryButtonProps, secondaryButtonProps } = useUnifiedButtonStyles();
    const { transition, hoverProps } = useUnifiedInteractiveCardStyles();
    const cardBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(15, 23, 42, 0.9)');
    const titleColor = useColorModeValue('navy.800', 'white');
    const accentColor = useColorModeValue('brand.500', 'brand.200');
    const categoryBadgeBg = useColorModeValue('#6B46F6', '#8B5CF6');
    const timelineLineColor = useColorModeValue('#DDD6FE', '#6D28D9');
    const floatingCardBg = useColorModeValue('white', 'navy.800');
    const neutralChipBg = useColorModeValue('#F8FAFC', 'whiteAlpha.120');
    const budgetBg = useColorModeValue('#EEF4FF', 'rgba(49, 130, 206, 0.16)');
    const budgetBadgeBg = useColorModeValue('#E8DDFF', 'rgba(139, 92, 246, 0.26)');
    const statusBadgeBg = useColorModeValue('#E8EEFF', 'rgba(72, 187, 255, 0.18)');
    const shadow = useColorModeValue('0 22px 50px rgba(15, 23, 42, 0.08)', '0 18px 40px rgba(2, 6, 23, 0.35)');

  const [isWatched, setIsWatched] = useState(listing.isWatched || false);
  const [watching, setWatching] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleWatch = async (e) => {
    e.stopPropagation();
    setWatching(true);
    try {
      const result = await toggleWatch(listing._id || listing.id);
      setIsWatched(result.watched);
    } catch {
      // silent fail
    }
    setWatching(false);
  };

  const listingId = listing._id || listing.id;
  const category = CATEGORIES.find(
    (option) => option.id === listing.category,
  );
  const quoteCount = listing.quoteCount ?? listing.quotesCount ?? 0;
  const clientPriceText = formatMoney(listing.clientPriceGross);
  const postedDate = listing.createdAt
    ? formatDateWithOptions(listing.createdAt, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';
  const fromCity = listing.fromCity || t('Not set');
  const fromCountry = listing.fromCountry || '';
  const toCity = listing.toCity || t('Not set');
  const toCountry = listing.toCountry || '';
  // In the search listing view, only show City + Postcode — full address is revealed on the listing detail page
  const fromLabel =
    [fromCity, listing.fromPostcode].filter(Boolean).join(', ') || t('Pick-up pending');
  const toLabel =
    [toCity, listing.toPostcode].filter(Boolean).join(', ') || t('Delivery pending');
  // Full labels (with country) used only in the map placeholder fallback
  const fromFullLabel = [fromCity, fromCountry].filter(Boolean).join(', ') || fromLabel;
  const toFullLabel = [toCity, toCountry].filter(Boolean).join(', ') || toLabel;
  const pickupLabel = formatDateRange(
    listing.pickupDateFrom,
    listing.pickupDateTo,
  );
  const deliveryLabel = formatDateRange(
    listing.deliveryDateFrom,
    listing.deliveryDateTo,
  );
  const clientTypeLabel =
    listing.clientType === 'company' ? t('Company') : t('Private Person');
  const item = getItemDetails(listing);
  const distanceText = formatDistance(
    listing.distanceKm || listing.routeDistanceKm,
  );
  const mapUrl = getMapThumbnailUrl(listing);
  const statusLabel = formatListingStatus(listing.status);
  const weightText = item.dimensions?.weight
    ? `${item.dimensions.weight} ${t('booking.kg')}`
    : null;
  const itemCountText = t('{{count}} item(s)', {
    count: item.dimensions?.itemCount || 0,
  });

  return (
    <UnifiedSurfaceCard
      bg={cardBg}
      p={{ base: 4, md: 5, xl: 6 }}
      borderRadius={{ base: '26px', xl: '34px' }}
      position="relative"
      transition={transition}
      boxShadow={shadow}
      border="1px solid"
      borderColor={subtleBorder}
      _hover={hoverProps}
    >
      <HStack
        position="absolute"
        top={{ base: 4, md: 5, xl: 6 }}
        right={{ base: 4, md: 5, xl: 6 }}
        align="center"
        spacing={3}
        zIndex={1}
        flexWrap="wrap"
        justify="flex-end"
      >
        {listing.humanId && (
          <Text
            fontSize="10px"
            color={mutedText}
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            #{listing.humanId}
          </Text>
        )}
        <Text
          fontSize="10px"
          color={mutedText}
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          {t('Posted')} {postedDate}
        </Text>
        <Badge
          bg={categoryBadgeBg}
          color="white"
          borderRadius="full"
          px={3.5}
          py={1}
          fontSize="xs"
          fontWeight="700"
          textTransform="none"
        >
          {t(category?.label || formatCategoryLabel(listing.category))}
        </Badge>
        <Badge
          bg={statusBadgeBg}
          color={accentColor}
          borderRadius="full"
          px={3.5}
          py={1}
          fontSize="xs"
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.04em"
        >
          {statusLabel}
        </Badge>
      </HStack>
      <Grid
        alignItems="center"
        templateColumns={{
          base: '1fr',
          xl: '220px minmax(260px, 320px) minmax(0, 1fr) 220px',
        }}
      >
        <Box
          w={{ base: '100%', xl: '220px' }}
          h={{ base: '160px', xl: '170px' }}
          borderRadius="22px"
          overflow="hidden"
          bg={subtleBg}
          border="1px solid"
          borderColor={subtleBorder}
          flexShrink={0}
          position="relative"
          boxShadow="0 14px 28px rgba(15, 23, 42, 0.08)"
        >
          {mapUrl ? (
            <Image
              src={mapUrl}
              alt={t('Route map')}
              w="100%"
              h="100%"
              objectFit="cover"
              fallback={
                <Flex w="100%" h="100%" align="center" justify="center">
                  <Icon
                    as={FaRoute}
                    boxSize={8}
                    color={accentColor}
                    opacity={0.5}
                  />
                </Flex>
              }
            />
          ) : (
            <Flex
              w="100%"
              h="100%"
              align="center"
              justify="center"
              direction="column"
              gap={2}
              px={4}
            >
              <Icon
                as={FaRoute}
                boxSize={8}
                color={accentColor}
                opacity={0.5}
              />
              <Text
                fontSize="sm"
                color={mutedText}
                textAlign="center"
                fontWeight="700"
              >
                {fromFullLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                textAlign="center"
                fontWeight="600"
              >
                {toFullLabel}
              </Text>
            </Flex>
          )}
        </Box>

        <HStack
          align="stretch"
          spacing={{ base: 3, md: 4 }}
          minW={0}
          flex={1}
          pl={5}
        >
          <VStack spacing={0} minW={{ base: '44px', md: '54px' }} pt={1}>
            <Flex
              w={{ base: '38px', md: '44px' }}
              h={{ base: '38px', md: '44px' }}
              align="center"
              justify="center"
              borderRadius="full"
              bg="#4285F4"
              color="white"
              fontWeight="800"
              fontSize={{ base: 'md', md: 'lg' }}
              boxShadow="0 8px 18px rgba(66, 133, 244, 0.25)"
            >
              A
            </Flex>
            <Box
              w="2px"
              flex="1"
              minH={{ base: '52px', md: '60px' }}
              bg={timelineLineColor}
              borderRadius="full"
              my={2}
              mx="auto"
            />
            <Flex
              w={{ base: '36px', md: '42px' }}
              h={{ base: '36px', md: '42px' }}
              align="center"
              justify="center"
              borderRadius="full"
              bg="#EA4335"
              color="white"
              fontWeight="800"
              fontSize={{ base: 'md', md: 'lg' }}
              boxShadow="0 14px 24px rgba(234, 67, 53, 0.26)"
            >
              B
            </Flex>
          </VStack>

          <VStack
            align="stretch"
            spacing={{ base: 6, md: 7 }}
            minW={0}
            justify="center"
          >
            <VStack align="start" spacing={0}>
              <Text
                fontSize="10px"
                fontWeight="800"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {t('Pick-up')}
              </Text>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="800"
                color={titleColor}
                lineHeight="1.3"
                noOfLines={1}
              >
                {fromLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                fontWeight="600"
              >
                {pickupLabel}
              </Text>
            </VStack>

            <VStack align="start" spacing={0}>
              <Text
                fontSize="10px"
                fontWeight="800"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {t('Delivery')}
              </Text>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="800"
                color={titleColor}
                lineHeight="1.3"
                noOfLines={1}
              >
                {toLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                fontWeight="600"
                noOfLines={1}
              >
                {deliveryLabel}
              </Text>
            </VStack>
          </VStack>
        </HStack>

        <VStack
          align="stretch"
          spacing={{ base: 4, md: 5 }}
          minW={0}
          justify="center"
          flex={1}
        >

          {/* Title */}
          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            fontWeight="700"
            color={titleColor}
            lineHeight="1.25"
            noOfLines={2}
            textTransform="capitalize"
          >
            {mapTemplateTitleToTranslation(listing.title || item.title, t)}
          </Text>

          <HStack spacing={3} flexWrap="wrap">
            <Box
              px={3.5}
              py={2}
              borderRadius="18px"
              bg={neutralChipBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="700"
                color={titleColor}
              >
                {clientTypeLabel}
              </Text>
            </Box>
            <Box
              px={3.5}
              py={2}
              borderRadius="18px"
              bg={neutralChipBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="700"
                color={titleColor}
              >
                {itemCountText}
              </Text>
            </Box>
            {listing.allowsQuotes === false ? (
              <Box
                px={3.5}
                py={2}
                borderRadius="18px"
                bg={neutralChipBg}
                border="1px solid"
                borderColor={subtleBorder}
              >
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="700"
                  color={titleColor}
                >
                  {t('Fixed price')}
                </Text>
              </Box>
            ) : null}
          </HStack>

                    <HStack
                        spacing={{ base: 3, md: 4 }}
                        flexWrap="wrap"
                        align="center"
                    >
                        {weightText ? (
                            <HStack spacing={1.5}>
                                <Icon as={MdScale} boxSize={3.5} color={accentColor} />
                                <Text fontSize="sm" color={titleColor} fontWeight="700">
                                    {weightText}
                                </Text>
                            </HStack>
                        ) : null}
                        {distanceText ? (
                            <HStack spacing={1.5}>
                                <Icon as={MdRoute} boxSize={3.5} color={accentColor} />
                                <Text fontSize="sm" color={titleColor} fontWeight="700">
                                    {distanceText}
                                </Text>
                            </HStack>
                        ) : null}
                    </HStack>

                    {clientPriceText ? (
                        <Flex
                            px={4}
                            py={3.5}
                            borderRadius="22px"
                            bg={budgetBg}
                            border="1px solid"
                            borderColor={subtleBorder}
                            align="center"
                            justify="space-between"
                            gap={4}
                            maxW={{ base: '100%', xl: '420px' }}
                        >
                            <Box>
                                <Text fontSize="10px" fontWeight="800" color={mutedText} textTransform="uppercase" letterSpacing="0.06em">
                                    {t('Client Price')}
                                </Text>
                                <Text fontSize={{ base:  'sm', md: 'md' }} fontWeight="900" color={titleColor} lineHeight="1.05">
                                    {clientPriceText}
                                </Text>
                            </Box>
                            <Badge
                                bg={budgetBadgeBg}
                                color="#3B1F9B"
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontSize="10px"
                                fontWeight="800"
                                textTransform="uppercase"
                                letterSpacing="0.04em"
                            >
                                {listing.allowsQuotes === false ? t('Fixed price') : t('Target budget')}
                            </Badge>
                        </Flex>
                    ) : null}
                </VStack>

                <VStack
                    align={{ base: 'stretch', lg: 'flex-end' }}
                    justify="center"
                    spacing={3}
                    minW={{ lg: '212px' }}
                    pt={{ lg: 8 }}
                >
                    <Button
                        {...primaryButtonProps}
                        fontSize="sm"
                        w="100%"
                        onClick={() => navigate(`/listings/${listingId}`)}
                    >
                        {listing.allowsQuotes === false ? t('Accept listing') : t('View details')}
                    </Button>

                    <HStack spacing={3} w="100%">
                        <Button
                            {...secondaryButtonProps}
                            size="sm"
                            flex="1"
                            rightIcon={<Icon as={isExpanded ? MdExpandLess : MdExpandMore} boxSize={4} />}
                            onClick={() => setIsExpanded((v) => !v)}
                        >
                            {t('quick view')}
                        </Button>
                        <IconButton
                            icon={<Icon as={isWatched ? MdBookmark : MdBookmarkBorder} boxSize={5} />}
                            aria-label={isWatched ? t('Unwatch') : t('Watch')}
                            variant="outline"
                            size="sm"
                            h="42px"
                            w="42px"
                            minW="42px"
                            borderRadius="16px"
                            borderColor={subtleBorder}
                            color={isWatched ? accentColor : mutedText}
                            bg={floatingCardBg}
                            isLoading={watching}
                            onClick={handleToggleWatch}
                        />
                    </HStack>

          {listing.allowsQuotes !== false && quoteCount > 0 ? (
            <Badge
              alignSelf={{ base: 'flex-start', lg: 'center' }}
              bg={neutralChipBg}
              color={titleColor}
              borderRadius="full"
              px={3}
              py={1}
              fontSize="10px"
              fontWeight="700"
              textTransform="none"
            >
              {t('{{count}} quote(s)', { count: quoteCount })}
            </Badge>
          ) : null}
        </VStack>
      </Grid>

      <Collapse in={isExpanded} animateOpacity>
        <Box mt={3}>
          <ListingItemsSection
            items={listing.items}
            category={listing.category}
          />
        </Box>
      </Collapse>
    </UnifiedSurfaceCard>
  );
}

export default function SearchListings() {
    const { t } = useTranslation();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [resolvedFromCoords, setResolvedFromCoords] = useState(null);
    const [resolvedToCoords, setResolvedToCoords] = useState(null);
    const [showWatched, setShowWatched] = useState(false);

    const isMobile = useBreakpointValue({ base: true, md: false });
    const inputBg = useColorModeValue('white', 'navy.800');
    const menuBg = useColorModeValue('rgba(255,255,255,0.98)', 'rgba(15,28,63,0.96)');
    const menuBorder = useColorModeValue('rgba(226,232,240,0.95)', 'rgba(255,255,255,0.14)');
    const modalBg = useColorModeValue('white', 'navy.800');
    const { mutedText } = useUnifiedSurfaceStyles();
    const { primaryButtonProps, secondaryButtonProps, ghostButtonProps } = useUnifiedButtonStyles();

  const debouncedFromCity = useDebounce(filters.fromCity, 300);
  const debouncedFromPostcode = useDebounce(filters.fromPostcode, 300);
  const debouncedToCity = useDebounce(filters.toCity, 300);
  const debouncedToPostcode = useDebounce(filters.toPostcode, 300);
  const debouncedSearch = useDebounce(filters.search, 300);

    // Geocode fromCity + fromCountry to coordinates for pickup radius search
    useEffect(() => {
        if (!debouncedFromCity || !filters.fromCountry) {
            setResolvedFromCoords(null);
            return;
        }
        const controller = new AbortController();
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(debouncedFromCity)},${encodeURIComponent(filters.fromCountry)}&format=json&limit=1`;
        fetch(url, {
            signal: controller.signal,
            headers: { 'User-Agent': 'LogiFlow/1.0' },
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data[0]) {
                    setResolvedFromCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                } else {
                    setResolvedFromCoords(null);
                }
            })
            .catch(() => setResolvedFromCoords(null));
        return () => controller.abort();
    }, [debouncedFromCity, filters.fromCountry]);

    // Geocode toCity + toCountry to coordinates for delivery radius search
    useEffect(() => {
        if (!debouncedToCity || !filters.toCountry) {
            setResolvedToCoords(null);
            return;
        }
        const controller = new AbortController();
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(debouncedToCity)},${encodeURIComponent(filters.toCountry)}&format=json&limit=1`;
        fetch(url, {
            signal: controller.signal,
            headers: { 'User-Agent': 'LogiFlow/1.0' },
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data[0]) {
                    setResolvedToCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                } else {
                    setResolvedToCoords(null);
                }
            })
            .catch(() => setResolvedToCoords(null));
        return () => controller.abort();
    }, [debouncedToCity, filters.toCountry]);

  const activeFilterChips = useMemo(
    () => getActiveFilterSummary(filters),
    [filters],
  );
  const hasActiveFilters = activeFilterChips.length > 0;

  useEffect(() => {
    const aiPrefill = consumePartnerMarketplaceFilters();
    if (!aiPrefill) {
      return;
    }

    setFilters((prev) => ({
      ...prev,
      ...aiPrefill,
      categories: Array.isArray(aiPrefill.categories)
        ? aiPrefill.categories
        : prev.categories,
    }));
  }, []);

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const toggleCategory = (categoryId) => {
    setFilters((prev) => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter((id) => id !== categoryId)
        : [...prev.categories, categoryId],
    }));
  };

    const buildParams = useCallback((pageNum) => {
        const params = { page: pageNum, limit: PAGE_SIZE };
        if (filters.categories.length) params.categories = filters.categories.join(',');
        if (filters.fromCountry) params.fromCountry = filters.fromCountry;
        if (debouncedFromCity) params.fromCity = debouncedFromCity;
        if (debouncedFromPostcode) params.fromPostcode = debouncedFromPostcode;
        if (filters.toCountry) params.toCountry = filters.toCountry;
        if (debouncedToCity) params.toCity = debouncedToCity;
        if (debouncedToPostcode) params.toPostcode = debouncedToPostcode;
        if (filters.pickupDateFrom) params.pickupDateFrom = filters.pickupDateFrom;
        if (filters.pickupDateTo) params.pickupDateTo = filters.pickupDateTo;
        if (debouncedSearch) params.search = debouncedSearch;
        // Use geocoded coords first; fall back to AI-provided coords in filter state
        const pickupCoords = resolvedFromCoords
            ?? (filters.fromLat && filters.fromLng
                ? { lat: Number(filters.fromLat), lng: Number(filters.fromLng) }
                : null);
        if (pickupCoords && filters.radiusKm && Number(filters.radiusKm) > 0) {
            params.fromLat = pickupCoords.lat;
            params.fromLng = pickupCoords.lng;
            params.radiusKm = filters.radiusKm;
        }

        const deliveryCoords = resolvedToCoords
            ?? (filters.toLat && filters.toLng
                ? { lat: Number(filters.toLat), lng: Number(filters.toLng) }
                : null);
        if (deliveryCoords && filters.toRadiusKm && Number(filters.toRadiusKm) > 0) {
            params.toLat = deliveryCoords.lat;
            params.toLng = deliveryCoords.lng;
            params.toRadiusKm = filters.toRadiusKm;
        }

        if (showWatched) params.watched = 'true';
        return params;
    }, [
        filters.categories,
        filters.fromCountry,
        filters.toCountry,
        filters.pickupDateFrom,
        filters.pickupDateTo,
        filters.radiusKm,
        filters.toRadiusKm,
        filters.fromLat,
        filters.fromLng,
        filters.toLat,
        filters.toLng,
        debouncedFromCity,
        debouncedFromPostcode,
        debouncedToCity,
        debouncedToPostcode,
        debouncedSearch,
        resolvedFromCoords,
        resolvedToCoords,
        showWatched,
    ]);

  const fetchListings = useCallback(
    async (pageNum, append = false) => {
      try {
        const data = await getListings(buildParams(pageNum));
        const items = Array.isArray(data)
          ? data
          : (data?.listings ?? data?.data ?? []);
        setListings((prev) => (append ? [...prev, ...items] : items));
        setHasMore(items.length === PAGE_SIZE);
      } catch {
        setError(t('Failed to load listings. Please try again.'));
      }
    },
    [buildParams, t],
  );

  useEffect(() => {
    setPage(1);
    setError(null);
    setLoading(true);
    fetchListings(1).finally(() => setLoading(false));
  }, [fetchListings]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    await fetchListings(nextPage, true);
    setPage(nextPage);
    setLoadingMore(false);
  };

  if (error) {
    return (
      <PageShell>
        <UnifiedSurfaceCard p={6}>
          <Text color="red.500">{error}</Text>
        </UnifiedSurfaceCard>
      </PageShell>
    );
  }

    return (
        <PageShell>
            <VStack align="stretch" spacing={6}>
                <PageHeader
                    title=""
                    description={t('Scan open marketplace requests quickly with a compact filter bar and richer delivery cards built for quoting decisions.')}
                    actions={
                        <AiAssistantDrawer
                            role="partner"
                            screenContext={{
                                routeKey: 'partner.marketplace',
                                role: 'partner',
                                surface: 'marketplace',
                            }}
                            buttonLabel={t('Search with AI')}
                            subtitle={t('Describe the lane you want and review the prepared marketplace filters.')}
                            onHandoff={(handoffRoute) => {
                                if (handoffRoute?.routeKey !== 'partner.marketplace') {
                                    return false;
                                }

                const nextFilters = handoffRoute?.params?.filters || {};
                setFilters((prev) => ({
                  ...prev,
                  ...nextFilters,
                  categories: Array.isArray(nextFilters.categories)
                    ? nextFilters.categories
                    : nextFilters.category
                      ? [nextFilters.category]
                      : prev.categories,
                }));
                return true;
              }}
            />
          }
        />

                <UnifiedPageToolbar
                    position="sticky"
                    top={{ base: '104px', md: '94px' }}
                    zIndex={3}
                >
                    <VStack align="stretch" spacing={4}>
                        <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3} flexWrap="wrap">
                            <HStack spacing={2} flexWrap="wrap">
                                <Menu closeOnSelect={false}>
                                    <MenuButton
                                        as={Button}
                                        {...secondaryButtonProps}
                                        rightIcon={<Icon as={MdExpandMore} />}
                                    >
                                        {getCategoriesLabel(filters.categories)}
                                    </MenuButton>
                                    <Portal>
                                        <MenuList
                                            minW="300px"
                                            p={2}
                                            zIndex={1600}
                                            bg={menuBg}
                                            border="1px solid"
                                            borderColor={menuBorder}
                                            boxShadow="0 24px 60px rgba(9, 30, 66, 0.22)"
                                        >
                                            <Flex justify="space-between" align="center" px={2} py={1}>
                                                <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase">
                                                    {t('Categories')}
                                                </Text>
                                                <HStack spacing={2}>
                                                    <Button
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="brand"
                                                        onClick={() => setFilter('categories', CATEGORIES.map((option) => option.id))}
                                                    >
                                                        {t('Select all')}
                                                    </Button>
                                                    <Button
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="gray"
                                                        onClick={() => setFilter('categories', [])}
                                                    >
                                                        {t('Clear')}
                                                    </Button>
                                                </HStack>
                                            </Flex>
                                            <MenuDivider />
                                            {CATEGORIES.map((category) => {
                                                const active = filters.categories.includes(category.id);
                                                return (
                                                    <MenuItem key={category.id} onClick={() => toggleCategory(category.id)} borderRadius="12px">
                                                        <HStack spacing={3} w="100%">
                                                            <Text fontSize="lg" lineHeight="1">{category.emoji}</Text>
                                                            <Text flex="1">{t(category.label)}</Text>
                                                            {active ? <Icon as={MdCheck} color="brand.400" />
                                                                : null}
                                                        </HStack>
                                                    </MenuItem>
                                                );
                                            })}
                                        </MenuList>
                                    </Portal>
                                </Menu>

                                <Button
                                    {...secondaryButtonProps}
                                    size="sm"
                                    variant={showWatched ? 'solid' : 'outline'}
                                    colorScheme={showWatched ? 'orange' : 'gray'}
                                    leftIcon={<Icon as={showWatched ? MdBookmark : MdBookmarkBorder} />}
                                    onClick={() => setShowWatched((v) => !v)}
                                >
                                    {showWatched ? t('Watching') : t('Watched')}
                                </Button>
                            </HStack>

              <UnifiedFilterBar p={2} gap={2} flex="1" justify="flex-end">
                <InputGroup
                  flex={{ base: '1 1 100%', md: '1 1 260px' }}
                  minW={{ md: '220px' }}
                >
                  <InputLeftElement pointerEvents="none" h="full">
                    <Icon as={MdSearch} color={mutedText} boxSize={4} />
                  </InputLeftElement>
                  <Input
                    bg={inputBg}
                    size="sm"
                    borderRadius="14px"
                    placeholder={t('Search title, city, description...')}
                    value={filters.search}
                    onChange={(e) => setFilter('search', e.target.value)}
                  />
                </InputGroup>

                                <Button
                                    {...secondaryButtonProps}
                                    size="sm"
                                    leftIcon={<Icon as={MdFilterList} />}
                                    colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                                    onClick={() => setFiltersOpen((value) => !value)}
                                >
                                    {filtersOpen ? t('Hide filters') : t('More filters')}
                                </Button>
                                {hasActiveFilters ? (
                                    <Button
                                        {...ghostButtonProps}
                                        size="sm"
                                        leftIcon={<Icon as={MdClose} />}
                                        onClick={() => setFilters(EMPTY_FILTERS)}
                                    >
                                        {t('Clear')}
                                    </Button>
                                ) : null}
                                <IconButton
                                    icon={<Icon as={MdRefresh} />}
                                    aria-label={t('Refresh')}
                                    {...ghostButtonProps}
                                    size="sm"
                                    isLoading={loading}
                                    onClick={() => fetchListings(1)}
                                />
                            </UnifiedFilterBar>
                        </Flex>

            {activeFilterChips.length ? (
              <Flex gap={2} wrap="wrap">
                {activeFilterChips.map((chip) => (
                  <Badge
                    key={chip.key}
                    colorScheme="brand"
                    borderRadius="full"
                    px={3}
                    py={1.5}
                  >
                    {chip.label}
                  </Badge>
                ))}
              </Flex>
            ) : (
              <Badge
                alignSelf="flex-start"
                colorScheme="brand"
                variant="subtle"
                borderRadius="full"
                px={3}
                py={1.5}
              >
                {t('Marketplace view')}
              </Badge>
            )}

                        {/* Desktop: inline collapse */}
                        {!isMobile && (
                            <Collapse in={filtersOpen} animateOpacity>
                                <UnifiedFilterBar mt={1}>
                                    <FilterFields
                                        filters={filters}
                                        setFilter={setFilter}
                                        inputBg={inputBg}
                                        debouncedToCity={debouncedToCity}
                                        t={t}
                                    />
                                </UnifiedFilterBar>
                            </Collapse>
                        )}

                        {/* Mobile: full-screen modal */}
                        {isMobile && (
                            <Modal
                                isOpen={filtersOpen}
                                onClose={() => setFiltersOpen(false)}
                                size="full"
                                motionPreset="slideInBottom"
                            >
                                <ModalOverlay />
                                <ModalContent bg={modalBg} borderTopRadius="24px" mt="auto" mb={0} maxH="92vh">
                                    <ModalHeader fontSize="md" fontWeight="800" pb={2}>
                                        {t('Filters')}
                                        {hasActiveFilters ? (
                                            <Badge ml={2} colorScheme="brand" borderRadius="full" fontSize="xs">
                                                {activeFilterChips.length}
                                            </Badge>
                                        ) : null}
                                    </ModalHeader>
                                    <ModalCloseButton />
                                    <ModalBody overflowY="auto" pb={4}>
                                        <FilterFields
                                            filters={filters}
                                            setFilter={setFilter}
                                            inputBg={inputBg}
                                            debouncedToCity={debouncedToCity}
                                            t={t}
                                        />
                                    </ModalBody>
                                    <ModalFooter gap={3} borderTop="1px solid" borderColor="whiteAlpha.200">
                                        <Button
                                            {...ghostButtonProps}
                                            flex="1"
                                            leftIcon={<Icon as={MdClose} />}
                                            onClick={() => { setFilters(EMPTY_FILTERS); setFiltersOpen(false); }}
                                        >
                                            {t('Clear all')}
                                        </Button>
                                        <Button
                                            {...primaryButtonProps}
                                            flex="1"
                                            onClick={() => setFiltersOpen(false)}
                                        >
                                            {t('Apply filters')}
                                        </Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>
                        )}
                    </VStack>
                </UnifiedPageToolbar>

                <UnifiedResultsSection
                    title={loading ? t('Searching marketplace...') : t('{{count}} open deliveries', { count: listings.length })}
                    actions={(
                        <HStack spacing={2} flexWrap="wrap">
                            <Badge colorScheme="brand" borderRadius="full" px={3} py={1.5}>
                                {getCategoriesLabel(filters.categories)}
                            </Badge>
                            {(filters.fromCountry || filters.fromCity || filters.toCountry || filters.toCity) ? (
                                <Badge colorScheme="brand" variant="subtle" borderRadius="full" px={3} py={1.5}>
                                    {t('Route filtered')}
                                </Badge>
                            ) : null}
                        </HStack>
                    )}
                >
                    {loading ? (
                        <UnifiedLoadingState label={t('Loading open deliveries...')} minH="40vh" />
                    ) : (
                        <>
                            <VStack spacing={4} align="stretch">
                                {listings.length === 0 ? (
                                    <UnifiedEmptyState
                                        title={t('No listings found')}
                                        description={t('Try widening the route, date, keyword, or category filters to see more marketplace opportunities.')}
                                        action={
                                            hasActiveFilters ? (
                                                <Button {...primaryButtonProps} onClick={() => setFilters(EMPTY_FILTERS)}>
                                                    {t('Clear filters')}
                                                </Button>
                                            ) : null
                                        }
                                    />
                                ) : (
                                    listings.map((listing, index) => (
                                        <ResultCard key={listing._id || listing.id || index} listing={listing} />
                                    ))
                                )}
                            </VStack>

                            {hasMore && listings.length > 0 ? (
                                <Flex justify="center" mt={8}>
                                    <Button {...primaryButtonProps} onClick={handleLoadMore} isLoading={loadingMore}>
                                        {t('Load more deliveries')}
                                    </Button>
                                </Flex>
                            ) : null}
                        </>
                    )}
                </UnifiedResultsSection>
            </VStack>
        </PageShell>
    );
}
